import React from 'react';
import styles from './index.module.less';
import type { ICartProduct, ICartProductData, IGetCartDataRes } from '@/interface/cart';
import { getCartData } from '@/api/client/cart';
import Loading from '../Loading';
import { ReoButton } from '@reolink-web/components.basic';
import classNames from 'classnames';
import { useTranslation } from '@/modules/ReoI18n';
import { AFTER_ADD_TO_CART_EVENT_KEY } from '@/common';
import { getOfficialSitePath } from '@/utils/function/sitePath';
interface ICompNavCartProps {
    lang: string;
    onCartUpdate?: (quantity: number) => void;
}

const CompNavCart: React.FC<ICompNavCartProps> = React.memo((props) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [cartData, setCartData] = React.useState<IGetCartDataRes>();
    const onCartUpdate = props.onCartUpdate;
    const { t } = useTranslation();

    const getTextByKey = React.useCallback(
        (key: string) => {
            const text = t(`reolink-header:${key}`);
            return text ? text : key;
        },
        [t],
    );

    const cartProductList = React.useMemo(() => {
        if (!cartData) {
            return [];
        }

        const list = [];
        // eslint-disable-next-line @typescript-eslint/no-for-in-array
        for (const key in cartData.list) {
            if (key === 'normal' || key === 'single' || key === 'combination') {
                list.push(...(cartData.list[key] as unknown as ICartProduct[]));
                continue;
            }
            else if (key === 'amount') {
                list.push(
                    ...(cartData.list[key] as unknown as Required<ICartProductData>['amount'])
                        .map((item) => item.products)
                        .flat(),
                );
                continue;
            }
        }
        return list;
    }, [cartData]);

    const fetchCartData = React.useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await getCartData({
                lang: props.lang,
            });
            setCartData(res);

            let productQuantity = 0;

            // eslint-disable-next-line @typescript-eslint/no-for-in-array
            for (const key in res.list) {
                if (key === 'normal' || key === 'single' || key === 'combination') {
                    for (const item of res.list[key] as unknown as ICartProduct[]) {
                        productQuantity += item.quantity;
                    }
                }
                else if (key === 'amount') {
                    for (const item of (res.list[key] as unknown as Required<ICartProductData>['amount'])
                        .map((item) => item.products)
                        .flat()) {
                        productQuantity += item.quantity;
                    }
                }
            }
            onCartUpdate?.(productQuantity);
            setIsLoading(false);
        }
        catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
        finally {
            setIsLoading(false);
        }
    }, [onCartUpdate, props.lang]);

    React.useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        fetchCartData();
    }, [fetchCartData]);

    React.useEffect(() => {
        const handleAfterAddToCart = (): void => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            fetchCartData();
        };

        document.body.addEventListener(AFTER_ADD_TO_CART_EVENT_KEY, handleAfterAddToCart);
        return () => {
            document.body.removeEventListener(AFTER_ADD_TO_CART_EVENT_KEY, handleAfterAddToCart);
        };
    }, [fetchCartData]);

    return (
        <div
            className={ styles['nav-cart'] }
            // eslint-disable-next-line react/jsx-handler-names
            onScroll={ (e) => {
                e.stopPropagation();
                e.preventDefault();
            } }
        >
            {isLoading ? (
                <Loading
                    type={ 'partial' }
                    className={ styles['nav-cart-loading'] } />
            ) : (
                <div className={ styles['nav-cart-content'] }>
                    {cartProductList.length > 0 ? (
                        <div className={ styles['nav-cart-container'] }>
                            <ul className={ styles['product-list'] }>
                                {cartProductList.map((item, index) => {
                                    return (
                                        <React.Fragment key={ item.id }>
                                            <li className={ styles['product-item'] }>
                                                {item.combinations?.length ? (
                                                    <div className={ styles['product-item-bundles'] }>
                                                        {item.combinations.map((bundleItem) => {
                                                            return (
                                                                <a
                                                                    key={ bundleItem.id }
                                                                    href={ item.href }
                                                                    className={ styles['product-info'] }
                                                                >
                                                                    <img
                                                                        className={ styles['product-image'] }
                                                                        src={ bundleItem.productImage.pc }
                                                                        alt={ bundleItem.title }
                                                                    />
                                                                    <div className={ styles['product-text'] }>
                                                                        <h3 className={ styles['product-text-title'] }>
                                                                            {bundleItem.title}( x {bundleItem.amount} )
                                                                        </h3>
                                                                    </div>
                                                                </a>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <a
                                                        href={ item.href }
                                                        className={ styles['product-info'] }>
                                                        <img
                                                            className={ styles['product-image'] }
                                                            src={ item.productImage.pc }
                                                            alt={ item.title }
                                                        />
                                                        <div className={ styles['product-text'] }>
                                                            <h3 className={ styles['product-text-title'] }>
                                                                {item.title}
                                                            </h3>
                                                            <p className={ styles['product-text-version'] }>
                                                                {item.versionText}
                                                            </p>
                                                        </div>
                                                    </a>
                                                )}
                                                <div className={ styles['product-price'] }>
                                                    <span className={ styles['product-price-text'] }>
                                                        {item.quantity} X{' '}
                                                        {cartData!.currencySymbol.replace('{}', item.price)}
                                                    </span>
                                                </div>
                                            </li>
                                            {index !== cartProductList.length - 1 ? (
                                                <div className={ styles['product-deliver'] }></div>
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })}
                            </ul>
                            {cartData?.subTotal ? (
                                <div className={ styles['nav-cart-total'] }>
                                    <span className={ styles['nav-cart-total-title'] }>
                                        {getTextByKey('cart-subtotal')}:
                                    </span>
                                    <span className={ styles['nav-cart-total-price'] }>
                                        {cartData!.currencySymbol.replace('{}', cartData?.subTotal)}
                                    </span>
                                </div>
                            ) : null}
                            {cartData?.discount && Number(cartData.discount) > 0 ? (
                                <div className={ styles['nav-cart-discount'] }>
                                    <span className={ styles['nav-cart-discount-tag'] }>*</span>
                                    {getTextByKey('cart-save-money').replace(
                                        '{}',
                                        cartData!.currencySymbol.replace('{}', cartData?.discount),
                                    )}
                                </div>
                            ) : null}
                            <div className={ styles['nav-cart-footer'] }>
                                <a
                                    href={ getOfficialSitePath('/checkout/') }
                                    className={ styles['footer-link'] }>
                                    <ReoButton
                                        className={ classNames(
                                            styles['nav-cart-footer-button'],
                                            styles['nav-cart-footer-button-checkout'],
                                        ) }
                                    >
                                        {getTextByKey('cart-checkout')}
                                    </ReoButton>
                                </a>
                                <a
                                    href={ getOfficialSitePath('/cart/') }
                                    className={ styles['footer-link'] }>
                                    <ReoButton
                                        className={ classNames(
                                            styles['nav-cart-footer-button'],
                                            styles['nav-cart-footer-button-view-cart'],
                                        ) }
                                    >
                                        {getTextByKey('cart-view-cart')}
                                    </ReoButton>
                                </a>
                            </div>
                        </div>
                    ) : (
                        <div className={ styles['nav-cart-empty'] }>
                            <span>{getTextByKey('cart-empty')}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
});
export default CompNavCart;
